import React, { useState, useEffect } from 'react';
import { analyticsAPI, partnerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Trophy, Medal, TrendingUp, Users, DollarSign, Target, Award,
  ChevronUp, Percent, IndianRupee, Star, Zap, Crown, ArrowUp
} from 'lucide-react';

// Commission Ladder Tiers
const COMMISSION_TIERS = [
  { 
    tier: 'bronze', 
    name: 'Bronze', 
    minBookings: 0, 
    maxBookings: 2,
    commission: 1.5,
    color: 'bg-orange-100 text-orange-700 border-orange-300',
    bgGradient: 'from-orange-50 to-orange-100',
    icon: Medal,
    benefits: ['Basic support', '1.5% commission']
  },
  { 
    tier: 'silver', 
    name: 'Silver', 
    minBookings: 3, 
    maxBookings: 5,
    commission: 2.0,
    color: 'bg-slate-200 text-slate-700 border-slate-400',
    bgGradient: 'from-slate-50 to-slate-200',
    icon: Medal,
    benefits: ['Priority support', '2% commission', 'Early inventory access']
  },
  { 
    tier: 'gold', 
    name: 'Gold', 
    minBookings: 6, 
    maxBookings: 10,
    commission: 2.5,
    color: 'bg-amber-100 text-amber-700 border-amber-400',
    bgGradient: 'from-amber-50 to-amber-100',
    icon: Trophy,
    benefits: ['Dedicated manager', '2.5% commission', 'Exclusive projects', 'Co-branded materials']
  },
  { 
    tier: 'platinum', 
    name: 'Platinum', 
    minBookings: 11, 
    maxBookings: Infinity,
    commission: 3.0,
    color: 'bg-violet-100 text-violet-700 border-violet-400',
    bgGradient: 'from-violet-50 to-violet-100',
    icon: Crown,
    benefits: ['VIP support', '3% commission', 'First access to launches', 'Quarterly bonus', 'Annual awards']
  },
];

const getTierByBookings = (bookings) => {
  return COMMISSION_TIERS.find(t => bookings >= t.minBookings && bookings <= t.maxBookings) || COMMISSION_TIERS[0];
};

const getNextTier = (currentTier) => {
  const idx = COMMISSION_TIERS.findIndex(t => t.tier === currentTier);
  return idx < COMMISSION_TIERS.length - 1 ? COMMISSION_TIERS[idx + 1] : null;
};

const LeaderboardPage = () => {
  const [partners, setPartners] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [period, setPeriod] = useState('monthly');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      const [performanceRes, partnersRes] = await Promise.all([
        analyticsAPI.getPartnerPerformance(period),
        partnerAPI.list('approved'),
      ]);
      setPerformance(performanceRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      toast.error('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return { icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-100' };
    if (rank === 2) return { icon: Medal, color: 'text-slate-400', bg: 'bg-slate-100' };
    if (rank === 3) return { icon: Medal, color: 'text-orange-400', bg: 'bg-orange-100' };
    return { icon: null, color: 'text-muted-foreground', bg: 'bg-muted' };
  };

  // Calculate totals
  const totals = performance.reduce((acc, p) => ({
    leads: acc.leads + (p.total_leads || 0),
    bookings: acc.bookings + (p.bookings || 0),
    revenue: acc.revenue + (p.total_revenue || 0),
    commission: acc.commission + (p.total_commission || 0),
  }), { leads: 0, bookings: 0, revenue: 0, commission: 0 });

  // Top performers
  const topByBookings = [...performance].sort((a, b) => b.bookings - a.bookings).slice(0, 3);
  const topByRevenue = [...performance].sort((a, b) => b.total_revenue - a.total_revenue).slice(0, 3);
  const topByConversion = [...performance].sort((a, b) => b.conversion_rate - a.conversion_rate).slice(0, 3);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="h-7 w-7 text-amber-500" />
            Agent Leaderboard
          </h1>
          <p className="text-muted-foreground">Performance rankings & commission ladder</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={period === 'monthly' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setPeriod('monthly')}
          >
            This Month
          </Button>
          <Button 
            variant={period === 'quarterly' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setPeriod('quarterly')}
          >
            This Quarter
          </Button>
          <Button 
            variant={period === 'yearly' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setPeriod('yearly')}
          >
            This Year
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Agents</span>
            </div>
            <p className="text-2xl font-bold">{performance.length}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Target className="h-4 w-4" />
              <span className="text-sm">Total Bookings</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{totals.bookings}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <IndianRupee className="h-4 w-4" />
              <span className="text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totals.revenue)}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-sm">Total Commission</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totals.commission)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="leaderboard">
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="ladder">
            <Zap className="h-4 w-4 mr-2" />
            Commission Ladder
          </TabsTrigger>
          <TabsTrigger value="achievements">
            <Award className="h-4 w-4 mr-2" />
            Top Performers
          </TabsTrigger>
        </TabsList>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="mt-6">
          {performance.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trophy className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No performance data yet</p>
                <p className="text-muted-foreground">Agents will appear here as they make bookings</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {performance.map((agent, index) => {
                const rank = agent.ranking || index + 1;
                const rankDisplay = getRankDisplay(rank);
                const tier = getTierByBookings(agent.bookings);
                const nextTier = getNextTier(tier.tier);
                const TierIcon = tier.icon;
                const RankIcon = rankDisplay.icon;

                return (
                  <Card 
                    key={agent.partner_id} 
                    className={`summary-card ${rank <= 3 ? 'border-2' : ''} ${
                      rank === 1 ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-white' :
                      rank === 2 ? 'border-slate-300 bg-gradient-to-r from-slate-50 to-white' :
                      rank === 3 ? 'border-orange-300 bg-gradient-to-r from-orange-50 to-white' : ''
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Rank */}
                        <div className={`h-12 w-12 rounded-full ${rankDisplay.bg} flex items-center justify-center shrink-0`}>
                          {RankIcon ? (
                            <RankIcon className={`h-6 w-6 ${rankDisplay.color}`} />
                          ) : (
                            <span className="text-lg font-bold text-muted-foreground">#{rank}</span>
                          )}
                        </div>

                        {/* Agent Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-lg">{agent.partner_name}</h3>
                            <Badge className={tier.color}>
                              <TierIcon className="h-3 w-3 mr-1" />
                              {tier.name}
                            </Badge>
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                              {tier.commission}% Commission
                            </Badge>
                          </div>
                          
                          {/* Progress to next tier */}
                          {nextTier && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground">
                                  {nextTier.minBookings - agent.bookings} more booking(s) to {nextTier.name}
                                </span>
                                <span className="text-emerald-600 font-medium">
                                  <ArrowUp className="h-3 w-3 inline" /> {nextTier.commission}%
                                </span>
                              </div>
                              <Progress 
                                value={(agent.bookings / nextTier.minBookings) * 100} 
                                className="h-2"
                              />
                            </div>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-xl font-bold">{agent.total_leads}</p>
                            <p className="text-xs text-muted-foreground">Leads</p>
                          </div>
                          <div>
                            <p className="text-xl font-bold">{agent.site_visits}</p>
                            <p className="text-xs text-muted-foreground">Visits</p>
                          </div>
                          <div>
                            <p className="text-xl font-bold text-emerald-600">{agent.bookings}</p>
                            <p className="text-xs text-muted-foreground">Bookings</p>
                          </div>
                          <div>
                            <p className="text-xl font-bold">{agent.conversion_rate}%</p>
                            <p className="text-xs text-muted-foreground">Conversion</p>
                          </div>
                        </div>

                        {/* Revenue */}
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold">{formatCurrency(agent.total_revenue)}</p>
                          <p className="text-sm text-emerald-600">{formatCurrency(agent.total_commission)} earned</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Commission Ladder Tab */}
        <TabsContent value="ladder" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {COMMISSION_TIERS.map((tier, index) => {
              const TierIcon = tier.icon;
              const agentsInTier = performance.filter(p => {
                const t = getTierByBookings(p.bookings);
                return t.tier === tier.tier;
              }).length;

              return (
                <Card 
                  key={tier.tier} 
                  className={`relative overflow-hidden border-2 ${tier.color.split(' ')[2]}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-b ${tier.bgGradient} opacity-50`} />
                  <CardContent className="relative p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`h-14 w-14 rounded-full ${tier.color.split(' ')[0]} flex items-center justify-center`}>
                        <TierIcon className={`h-7 w-7 ${tier.color.split(' ')[1]}`} />
                      </div>
                      <Badge variant="outline" className="text-lg font-bold">
                        {tier.commission}%
                      </Badge>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {tier.minBookings === 0 
                        ? `0-${tier.maxBookings} bookings` 
                        : tier.maxBookings === Infinity 
                          ? `${tier.minBookings}+ bookings`
                          : `${tier.minBookings}-${tier.maxBookings} bookings`
                      }
                    </p>
                    
                    <div className="border-t pt-4 mt-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2">BENEFITS</p>
                      <ul className="space-y-1">
                        {tier.benefits.map((benefit, i) => (
                          <li key={i} className="text-sm flex items-center gap-2">
                            <Star className="h-3 w-3 text-amber-500" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm">
                        <span className="font-bold text-lg">{agentsInTier}</span>
                        <span className="text-muted-foreground"> agent(s) in this tier</span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Commission Calculator */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Commission Calculator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Booking Value</th>
                      {COMMISSION_TIERS.map(tier => (
                        <th key={tier.tier} className="text-center py-3 px-4">
                          <Badge className={tier.color}>{tier.name}</Badge>
                          <div className="text-xs mt-1">{tier.commission}%</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[5000000, 7500000, 10000000, 15000000, 20000000].map(value => (
                      <tr key={value} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{formatCurrency(value)}</td>
                        {COMMISSION_TIERS.map(tier => (
                          <td key={tier.tier} className="text-center py-3 px-4 font-semibold text-emerald-600">
                            {formatCurrency(value * tier.commission / 100)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Performers Tab */}
        <TabsContent value="achievements" className="mt-6">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Top by Bookings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="h-5 w-5 text-emerald-600" />
                  Top Closers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topByBookings.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {topByBookings.map((agent, i) => (
                      <div key={agent.partner_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          i === 0 ? 'bg-amber-100' : i === 1 ? 'bg-slate-200' : 'bg-orange-100'
                        }`}>
                          <span className="font-bold text-sm">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{agent.partner_name}</p>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {agent.bookings} bookings
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top by Revenue */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <IndianRupee className="h-5 w-5 text-blue-600" />
                  Top Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topByRevenue.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {topByRevenue.map((agent, i) => (
                      <div key={agent.partner_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          i === 0 ? 'bg-amber-100' : i === 1 ? 'bg-slate-200' : 'bg-orange-100'
                        }`}>
                          <span className="font-bold text-sm">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{agent.partner_name}</p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">
                          {formatCurrency(agent.total_revenue)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top by Conversion */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-5 w-5 text-violet-600" />
                  Best Conversion
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topByConversion.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {topByConversion.map((agent, i) => (
                      <div key={agent.partner_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          i === 0 ? 'bg-amber-100' : i === 1 ? 'bg-slate-200' : 'bg-orange-100'
                        }`}>
                          <span className="font-bold text-sm">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{agent.partner_name}</p>
                        </div>
                        <Badge className="bg-violet-100 text-violet-700">
                          {agent.conversion_rate}%
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeaderboardPage;
